import { Box, Button, Drawer, IconButton, Typography } from '@mui/material'
import { IconSearch, IconX } from '@tabler/icons-react'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { CheckboxMultiple } from '~/components/Form/CheckboxMultiple'
import { Input } from '~/components/Form/Input'
import { SearchQueysData, useQueryString } from '~/hooks'
import { ProposalContractOriginOptions, contractPendenciesOptions, contractStatusOptions } from '~/utils/options'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { EProposalOrigin } from '~/graphql/types'

type FilterData = {
  name?: string,
  ucOrClientNumber?: string,
  oldInstallationNumber?: string,
  status?: string[],
  pendencies?: string[],
  origin?: string[],
  canCreateInvoices?: string[],
  begin?: string,
  end?: string
}

type FilterContractListDrawerProps = {
  drawerIsOpen: boolean
  setDrawerIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  toggleDrawer: () => void
  queryKeys: SearchQueysData[]
}

const schema = yup
  .object({
    begin: yup.string().test('isValid', (value, validation) => {

      if (value && Boolean(!validation.parent.end)) {
        return validation.createError({
          message: 'Este campo é obrigatório.',
          path: 'end'
        })
      }
      return true
    }),
    end: yup.string()
      .test('isValid', (value, validation) => {
        if (value && Boolean(!validation.parent.begin)) {
          return validation.createError({
            message: 'Este campo é obrigatório.',
            path: 'begin'
          })
        }
        return true
      }),
  })
  .required()

export const FilterContractListDrawer: React.FC<FilterContractListDrawerProps> = ({ drawerIsOpen, toggleDrawer, setDrawerIsOpen, queryKeys }) => {
  const formMethods = useForm({
    resolver: yupResolver(schema)
  })

  const { searchQuerys, setSearchParams } = useQueryString()
  const queryValues = searchQuerys(queryKeys)
  const watchCanCreateInvoices = formMethods.watch('canCreateInvoices')

  useEffect(() => {
    if (drawerIsOpen) {
      formMethods.setValue('ucOrClientNumber', queryValues.ucOrClientNumber)
      formMethods.setValue('oldInstallationNumber', queryValues.oldInstallationNumber)
      formMethods.setValue('begin', queryValues.begin)
      formMethods.setValue('end', queryValues.end)
      formMethods.setValue('status', queryValues.status)
      formMethods.setValue('pendencies', queryValues.pendencies)
      formMethods.setValue('origin', queryValues.origin)
      formMethods.setValue('canCreateInvoices', queryValues.canCreateInvoices)
    }
  }, [drawerIsOpen])

  // Garante que apenas uma opção seja selecionada em canCreateInvoices
  useEffect(() => {
    if (watchCanCreateInvoices?.length > 1) {
      formMethods.setValue('canCreateInvoices', [watchCanCreateInvoices[watchCanCreateInvoices.length - 1]])
    }
  }, [watchCanCreateInvoices])

  const handleFilters = (filters: FilterData) => {
    setDrawerIsOpen(false)
    setSearchParams(state => {
      state.set('page', '0')
      if (filters?.status && filters?.status?.length > 0 || queryValues?.status?.length > 0) {
        state.set('status', JSON.stringify(filters.status) || queryValues.status)
      } else {
        state.delete('status')
      }
      if (filters?.pendencies && filters?.pendencies?.length > 0 || queryValues?.pendencies?.length > 0) {
        state.set('pendencies', JSON.stringify(filters.pendencies) || queryValues.pendencies)
      } else {
        state.delete('pendencies')
      }
      if (filters?.origin && filters?.origin?.length > 0 || queryValues?.origin?.length > 0) {
        state.set('origin', JSON.stringify(filters.origin) || queryValues.origin)
      } else {
        state.delete('origin')
      }
      if (filters?.canCreateInvoices && filters?.canCreateInvoices?.length > 0 || queryValues?.canCreateInvoices?.length > 0) {
        state.set('canCreateInvoices', JSON.stringify(filters.canCreateInvoices) || queryValues.canCreateInvoices)
      } else {
        state.delete('canCreateInvoices')
      }
      filters.ucOrClientNumber || queryValues.ucOrClientNumber ? state.set('ucOrClientNumber', filters.ucOrClientNumber || queryValues.ucOrClientNumber) : state.delete('ucOrClientNumber')
      filters.oldInstallationNumber || queryValues.oldInstallationNumber ? state.set('oldInstallationNumber', filters.oldInstallationNumber || queryValues.oldInstallationNumber) : state.delete('oldInstallationNumber')
      filters.begin || queryValues.begin ? state.set('begin', filters.begin || queryValues.begin) : state.delete('begin')
      filters.end || queryValues.end ? state.set('end', filters.end || queryValues.end) : state.delete('end')
      return state
    })
  }

  const OriginOptions = ProposalContractOriginOptions.map((item) => {

    let category: null | string = null
    if (item.value === EProposalOrigin.partnerLink) {
      category = 'Parceiro'
    }
    if (item.value === EProposalOrigin.website) {
      category = 'Autoatendimento'
    }
    if (item.value === EProposalOrigin.internal) {
      category = 'Backoffice'
    }
    if (item.value === EProposalOrigin.customer) {
      category = 'Cliente'
    }

    return {
      ...item,
      ...category ? { category } : {}
    }
  })

  const onRemoveFilters = () => {
    setDrawerIsOpen(false)
    setSearchParams(state => {
      state.set('page', '0')
      state.delete('ucOrClientNumber')
      state.delete('oldInstallationNumber')
      state.delete('status')
      state.delete('pendencies')
      state.delete('origin')
      state.delete('canCreateInvoices')
      state.delete('begin')
      state.delete('end')
      return state
    })
  }

  return (
    <Drawer PaperProps={{ sx: { maxWidth: '560px', width: '100%', padding: '2rem' } }} anchor='right' open={drawerIsOpen} onClose={toggleDrawer}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography fontWeight={500} variant='h5'>Filtrar por</Typography>
        <IconButton type='button' onClick={() => toggleDrawer()}>
          <IconX />
        </IconButton>
      </Box>

      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit((formData) => handleFilters({ ...formData, name: queryValues.name || '' }))}>
          <Typography sx={{ paddingTop: '1rem' }} fontWeight={500}>Data de solicitação</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 0 0 0' }}>
            <Input name='begin' type='date' />
            <Typography>Até</Typography>
            <Input name='end' type='date' />
          </Box>
          <Typography sx={{ padding: '2rem 0 .6rem 0' }} fontWeight={500}>UC ou número de cliente</Typography>
          <Input icons={{ end: { element: <IconSearch /> } }} label='UC / Número do cliente' name='ucOrClientNumber' />
          <Typography sx={{ padding: '2rem 0 .6rem 0' }} fontWeight={500}>Número de instalação antigo</Typography>
          <Input icons={{ end: { element: <IconSearch /> } }} label='Número de instalação antigo' name='oldInstallationNumber' />
          <Typography padding='2rem 0 0 0' fontWeight={500}>Status</Typography>
          <CheckboxMultiple name='status' options={contractStatusOptions} />
          <Typography sx={{ paddingTop: '1.4rem' }} fontWeight={500}>Pendencias</Typography>
          <CheckboxMultiple name='pendencies' options={contractPendenciesOptions} />

          <Typography sx={{ margin: '2rem 0 0 0' }} fontWeight={500}>Origem</Typography>
          <CheckboxMultiple name='origin' options={OriginOptions} />

          <Typography sx={{ margin: '2rem 0 0 0' }} fontWeight={500}>Permitir criar novas faturas?</Typography>
          <CheckboxMultiple name='canCreateInvoices' options={[
            { value: 'true', label: 'Sim' },
            { value: 'false', label: 'Não' }
          ]} />

          <Box sx={{ display: 'flex', padding: '2rem 0', gap: '1rem', justifyContent: 'flex-end' }}>
            <Button onClick={() => onRemoveFilters()} type='button' fullWidth color='secondary'>Limpar</Button>
            <Button type='submit' fullWidth color='primary'>Aplicar</Button>
          </Box>
        </form>
      </FormProvider>
    </Drawer>
  )
}
